import { useEffect, useState, useCallback } from 'react';
import {
  Calendar as CalendarIcon,
  Clock,
  Video,
  Plus,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  User as UserIcon,
  Globe,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { BookMeetingDrawer } from './BookMeetingDrawer';
import { MeetingRecordingPanel } from './MeetingRecordingPanel';
import { formatDate, formatDateTime, formatTime } from '@/lib/dateFormat';
import { convertWallClock, tzLabel, wallClockToUtcMs } from '@/lib/tz';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { TimeDropdown } from '@/components/TimeDropdown';

// ─── Types ────────────────────────────────────────────────────────────────

interface RemarkActivity {
  id: string;
  actionType: string;
  payload: any;
  createdAt: string;
  actor?: { id: string; name: string } | null;
}

interface MeetingRow {
  id: string;
  eventId: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string | null;
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;           // guest's TZ (what they picked at booking)
  counselorTimezone?: string; // counselor's own availability TZ (the attendee)
  status: string;
  notes: string | null;
  meetingLink: string | null;
  rescheduleCount: number;
  createdAt: string;
  // Zoom attendance (Phase 2) — filled by Zoom webhooks.
  startedAt?: string | null;
  endedAt?: string | null;
  hostJoinedAt?: string | null;
  guestJoinedAt?: string | null;
  firstJoinedRole?: string | null; // 'host' | 'attendee'
  transcript?: string | null;
  transcriptStatus?: string | null; // 'pending' | 'ready'
  recordingUrl?: string | null;
  zoomMeetingId?: string | null;
  audioStatus?: string | null;      // 'ready' | 'failed' | 'too_large'
  aiSummary?: string | null;
  aiSummaryStatus?: string | null;  // 'ready' | 'failed' | 'no_ai'
  event: { name: string; color: string; meetingProvider: string | null };
  user: { id: string; name: string; email: string } | null;
  remarks: RemarkActivity[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmt12(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Format an absolute instant (ISO) as a wall-clock time in the given IANA tz.
function fmtInstant(iso?: string | null, tz?: string): string {
  if (!iso) return '';
  return formatTime(iso, tz || undefined);
}

const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  CONFIRMED: { label: 'Confirmed', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  COMPLETED: { label: 'Completed', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  CANCELLED: { label: 'Cancelled', cls: 'bg-red-50 text-red-700 border-red-200' },
  NO_SHOW:   { label: 'No-show',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  RESCHEDULED: { label: 'Rescheduled', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

// ─── MeetingsTab (default export component) ───────────────────────────────

export function MeetingsTab({ leadId, hasAssignedCounselor }: { leadId: string; hasAssignedCounselor: boolean }) {
  const [meetings, setMeetings] = useState<MeetingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBook, setShowBook] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${leadId}/meetings`);
      setMeetings(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {/* Header row — count + Book Meeting CTA */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-[#6366F1]" />
            Meetings
            <span className="text-sm font-normal text-slate-400">({meetings.length})</span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            All bookings for this lead — past + upcoming. Add remarks like a followup activity.
          </p>
        </div>
        <button
          onClick={() => setShowBook(true)}
          disabled={!hasAssignedCounselor}
          title={hasAssignedCounselor ? 'Book a meeting on the assigned counselor\'s calendar' : 'Assign a counselor to this lead first'}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed px-3.5 py-2 text-sm font-semibold text-white shadow-sm"
        >
          <Plus className="h-4 w-4" /> Book Meeting
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-12 text-center">
          <CalendarIcon className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm font-semibold text-slate-700">No meetings yet</p>
          <p className="text-xs text-slate-500 mt-1">
            {hasAssignedCounselor
              ? 'Click Book Meeting above to schedule a demo or consultation.'
              : 'Assign a counselor to this lead first, then a meeting can be booked.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => (
            <MeetingCard key={m.id} leadId={leadId} meeting={m} onChange={load} />
          ))}
        </div>
      )}

      {showBook && (
        <BookMeetingDrawer
          leadId={leadId}
          onClose={() => setShowBook(false)}
          onBooked={() => { setShowBook(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Per-meeting card with remarks + actions ─────────────────────────────

function MeetingCard({
  leadId,
  meeting,
  onChange,
}: {
  leadId: string;
  meeting: MeetingRow;
  onChange: () => void;
}) {
  const [remark, setRemark] = useState('');
  const [postingRemark, setPostingRemark] = useState(false);
  const [changingStatus, setChangingStatus] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({ date: meeting.date, startTime: meeting.startTime });
  const [showReschedule, setShowReschedule] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showComplete, setShowComplete] = useState(false);
  const [completeRemark, setCompleteRemark] = useState('');
  // No-show used to fire immediately on click — an accidental tap
  // silently marked the meeting as NO_SHOW. Now it opens an inline
  // confirm strip (matches the Cancel / Complete pattern).
  const [showNoShow, setShowNoShow] = useState(false);
  const [noShowRemark, setNoShowRemark] = useState('');

  const status = STATUS_STYLE[meeting.status] ?? STATUS_STYLE.CONFIRMED;

  const submitRemark = async () => {
    if (!remark.trim()) return;
    setPostingRemark(true);
    try {
      await api.post(`/leads/${leadId}/meetings/${meeting.id}/remarks`, { remark: remark.trim() });
      setRemark('');
      toast('success', 'Remark added');
      onChange();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Could not add remark');
    } finally {
      setPostingRemark(false);
    }
  };

  const setStatus = async (next: string, cancelReason?: string) => {
    setChangingStatus(next);
    try {
      await api.patch(`/leads/${leadId}/meetings/${meeting.id}/status`, {
        status: next,
        cancelReason,
      });
      toast('success', `Marked as ${STATUS_STYLE[next]?.label ?? next}`);
      onChange();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to update status');
    } finally {
      setChangingStatus(null);
    }
  };

  const submitReschedule = async () => {
    if (!rescheduleForm.date || !rescheduleForm.startTime) return;
    setRescheduling(true);
    try {
      await api.patch(`/leads/${leadId}/meetings/${meeting.id}/reschedule`, rescheduleForm);
      toast('success', 'Meeting rescheduled');
      setShowReschedule(false);
      onChange();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Top color strip (matches Time Availability event colour) */}
      <div className="h-1" style={{ backgroundColor: meeting.event.color }} />

      <div className="p-4">
        {/* Header: event + status + date/time */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-slate-900">{meeting.event.name}</p>
              <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border ${status.cls}`}>
                {status.label}
              </span>
              {meeting.rescheduleCount > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500">
                  <RefreshCw className="h-3 w-3" />
                  Rescheduled ×{meeting.rescheduleCount}
                </span>
              )}
            </div>
            <div className="mt-1 space-y-1 text-xs text-slate-500">
              {/* Date + counselor (attendee) */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <CalendarIcon className="h-3.5 w-3.5" /> {formatDate(meeting.date)}
                </span>
                {meeting.user && (
                  <span className="inline-flex items-center gap-1">
                    <UserIcon className="h-3.5 w-3.5" /> {meeting.user.name}
                  </span>
                )}
              </div>
              {/* Time — counselor's TZ primary (the attendee), guest's TZ for
                  context. startTime/endTime are stored in the counselor's
                  wall-clock; `timezone` is the guest's TZ. */}
              {(() => {
                const counselorTz = meeting.counselorTimezone;
                // Old bookings (before counselorTimezone was returned) fall
                // back to the original single-line display.
                if (!counselorTz) {
                  return (
                    <div className="flex items-center gap-1 flex-wrap">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="font-medium text-slate-700">{fmt12(meeting.startTime)} – {fmt12(meeting.endTime)}</span>
                      <span className="text-slate-400">({meeting.timezone})</span>
                    </div>
                  );
                }
                const guestTz = meeting.timezone;
                const atUtc = wallClockToUtcMs(meeting.date, meeting.startTime, counselorTz);
                const guest = guestTz && guestTz !== counselorTz
                  ? convertWallClock(meeting.date, meeting.startTime, counselorTz, guestTz)
                  : null;
                // Hide the guest line when it renders the exact same wall-clock
                // as the counselor's — e.g. Asia/Calcutta vs Asia/Kolkata
                // (aliases) or any two zones sharing the offset at this instant.
                const sameWall = !!guest && guest.date === meeting.date && guest.time === meeting.startTime;
                return (
                  <>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="font-semibold text-slate-800">{fmt12(meeting.startTime)} – {fmt12(meeting.endTime)}</span>
                      <span className="text-slate-500">· {tzLabel(counselorTz, atUtc)}</span>
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">your time</span>
                    </div>
                    {guest && !sameWall && (
                      <div className="flex items-center gap-1.5 flex-wrap text-slate-400">
                        <Globe className="h-3.5 w-3.5" />
                        <span>Guest: {formatDate(guest.date)}, {fmt12(guest.time)} · {tzLabel(guestTz, atUtc)}</span>
                      </div>
                    )}
                  </>
                );
              })()}
              {/* Zoom attendance (Phase 2) — real start/end + who joined first. */}
              {(meeting.startedAt || meeting.endedAt || meeting.firstJoinedRole) && (
                <div className="flex items-center gap-1.5 flex-wrap text-slate-400">
                  <Video className="h-3.5 w-3.5" />
                  <span>
                    {meeting.startedAt && `Started ${fmtInstant(meeting.startedAt, meeting.counselorTimezone || meeting.timezone)}`}
                    {meeting.endedAt && ` · Ended ${fmtInstant(meeting.endedAt, meeting.counselorTimezone || meeting.timezone)}`}
                    {meeting.firstJoinedRole && ` · First in: ${meeting.firstJoinedRole === 'host' ? 'Counselor' : 'Guest'}`}
                  </span>
                </div>
              )}
            </div>
          </div>
          {/* Join stays visible ONLY on the active (CONFIRMED) meeting.
              A COMPLETED / CANCELLED / NO_SHOW / RESCHEDULED meeting is
              historical — server-side auto-supersede already guarantees
              at most one CONFIRMED per lead, so this shows the "latest
              active" join button and nothing else. */}
          {meeting.meetingLink && meeting.status === 'CONFIRMED' && (
            <a
              href={meeting.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg flex-shrink-0"
            >
              <Video className="h-3.5 w-3.5" /> Join
            </a>
          )}
        </div>

        {/* Notes from booking */}
        {meeting.notes && (
          <p className="mt-2 text-xs text-slate-600 bg-slate-50 rounded px-2.5 py-1.5 italic">
            "{meeting.notes}"
          </p>
        )}

        {/* Audio + AI summary + transcript (Zoom cloud recording) */}
        <MeetingRecordingPanel leadId={leadId} meeting={meeting} onChange={onChange} />

        {/* Remarks timeline */}
        {meeting.remarks.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Timeline</p>
            <div className="space-y-1.5 pl-3 border-l-2 border-slate-100">
              {meeting.remarks.map((r) => (
                <RemarkRow key={r.id} activity={r} bookingTz={meeting.timezone} />
              ))}
            </div>
          </div>
        )}

        {/* Remark input */}
        {meeting.status !== 'CANCELLED' && (
          <div className="mt-3 flex gap-2">
            <input
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitRemark()}
              placeholder="Add a remark or note about this meeting…"
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40"
            />
            <button
              onClick={submitRemark}
              disabled={!remark.trim() || postingRemark}
              className="rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 text-white text-xs font-semibold px-3 py-2"
            >
              {postingRemark ? '…' : 'Add'}
            </button>
          </div>
        )}

        {/* Actions */}
        {meeting.status === 'CONFIRMED' && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => { setShowReschedule(false); setShowCancel(false); setShowNoShow(false); setShowComplete((v) => !v); }}
              disabled={!!changingStatus}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded"
            >
              <CheckCircle2 className="h-3 w-3" /> Completed
            </button>
            <button
              onClick={() => { setShowCancel(false); setShowComplete(false); setShowNoShow(false); setShowReschedule((v) => !v); }}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded"
            >
              <RefreshCw className="h-3 w-3" /> Reschedule
            </button>
            <button
              onClick={() => { setShowReschedule(false); setShowComplete(false); setShowCancel(false); setShowNoShow((v) => !v); }}
              disabled={!!changingStatus}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 px-2.5 py-1 rounded"
            >
              No-show
            </button>
            <button
              onClick={() => { setShowReschedule(false); setShowComplete(false); setShowNoShow(false); setShowCancel((v) => !v); }}
              disabled={!!changingStatus}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded"
            >
              <XCircle className="h-3 w-3" /> Cancel
            </button>
          </div>
        )}

        {/* Inline reschedule form */}
        {showReschedule && meeting.status === 'CONFIRMED' && (
          <div className="mt-3 flex items-end gap-2 flex-wrap p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div>
              <label className="block text-[10px] font-normal text-slate-500 uppercase mb-0.5">Date</label>
              <DateTimeInput
                mode="date"
                className="w-36"
                inputClassName="h-8 px-2 text-xs"
                clearable={false}
                value={rescheduleForm.date}
                onChange={(v) => setRescheduleForm((f) => ({ ...f, date: v }))}
              />
            </div>
            <div>
              <label className="block text-[10px] font-normal text-slate-500 uppercase mb-0.5">Start Time</label>
              <TimeDropdown
                className="w-32"
                value={rescheduleForm.startTime}
                onChange={(v) => setRescheduleForm((f) => ({ ...f, startTime: v }))}
              />
            </div>
            <button
              onClick={submitReschedule}
              disabled={rescheduling}
              className="text-xs font-semibold text-white bg-[#6366F1] hover:bg-[#4F46E5] px-3 py-1.5 rounded"
            >
              {rescheduling ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => setShowReschedule(false)}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Inline cancel-reason panel — replaces the native window.prompt.
            Behaves like the reschedule panel: stays on the Meetings tab. */}
        {showCancel && meeting.status === 'CONFIRMED' && (
          <div className="mt-3 p-3 bg-red-50/60 rounded-lg border border-red-100">
            <label className="block text-[10px] font-normal text-slate-500 uppercase mb-1">
              Cancel reason (optional)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={2}
              placeholder="Why is this meeting being cancelled?"
              className="w-full text-xs rounded border border-slate-200 px-2.5 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-red-200"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => {
                  setStatus('CANCELLED', cancelReason.trim() || undefined);
                  setShowCancel(false);
                  setCancelReason('');
                }}
                disabled={!!changingStatus}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 px-3 py-1.5 rounded"
              >
                <XCircle className="h-3.5 w-3.5" />
                {changingStatus === 'CANCELLED' ? 'Cancelling…' : 'Cancel meeting'}
              </button>
              <button
                onClick={() => { setShowCancel(false); setCancelReason(''); }}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5"
              >
                Keep meeting
              </button>
            </div>
          </div>
        )}

        {/* Inline completion-remark panel — lets the counselor jot how the
            meeting went. The remark lands on the timeline (and Followups). */}
        {showComplete && meeting.status === 'CONFIRMED' && (
          <div className="mt-3 p-3 bg-emerald-50/60 rounded-lg border border-emerald-100">
            <label className="block text-[10px] font-normal text-slate-500 uppercase mb-1">
              Completion remark (optional)
            </label>
            <textarea
              value={completeRemark}
              onChange={(e) => setCompleteRemark(e.target.value)}
              rows={2}
              placeholder="How did the meeting go? (added to the timeline)"
              className="w-full text-xs rounded border border-slate-200 px-2.5 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => {
                  setStatus('COMPLETED', completeRemark.trim() || undefined);
                  setShowComplete(false);
                  setCompleteRemark('');
                }}
                disabled={!!changingStatus}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 px-3 py-1.5 rounded"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {changingStatus === 'COMPLETED' ? 'Saving…' : 'Mark completed'}
              </button>
              <button
                onClick={() => { setShowComplete(false); setCompleteRemark(''); }}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Inline no-show confirm — used to fire on click without prompt,
            which meant an accidental tap silently marked the meeting as
            NO_SHOW. Now the user has to explicitly confirm, matching
            the Cancel / Complete pattern. Remark is optional. */}
        {showNoShow && meeting.status === 'CONFIRMED' && (
          <div className="mt-3 p-3 bg-amber-50/60 rounded-lg border border-amber-100">
            <p className="text-xs text-amber-800 mb-2">
              Mark this meeting as <b>no-show</b>? The status change goes to the timeline and can't be undone from here.
            </p>
            <label className="block text-[10px] font-normal text-slate-500 uppercase mb-1">
              Note (optional)
            </label>
            <textarea
              value={noShowRemark}
              onChange={(e) => setNoShowRemark(e.target.value)}
              rows={2}
              placeholder="Anything worth recording? (added to the timeline)"
              className="w-full text-xs rounded border border-slate-200 px-2.5 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-amber-200"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => {
                  setStatus('NO_SHOW', noShowRemark.trim() || undefined);
                  setShowNoShow(false);
                  setNoShowRemark('');
                }}
                disabled={!!changingStatus}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 px-3 py-1.5 rounded"
              >
                {changingStatus === 'NO_SHOW' ? 'Saving…' : 'Confirm no-show'}
              </button>
              <button
                onClick={() => { setShowNoShow(false); setNoShowRemark(''); }}
                className="text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RemarkRow({ activity, bookingTz }: { activity: RemarkActivity; bookingTz?: string }) {
  const p = activity.payload ?? {};
  let icon = <UserIcon className="h-3 w-3 text-slate-400" />;
  let text: React.ReactNode = null;
  switch (activity.actionType) {
    case 'meeting_remark':
      text = <span className="text-slate-700">{p.remark}</span>;
      break;
    case 'meeting_status_changed':
      text = <span className="text-slate-500">Status: {p.from} → <b>{p.to}</b>{p.reason ? ` — ${p.reason}` : ''}</span>;
      break;
    case 'meeting_rescheduled': {
      // Show old/new slot in the booking's own TZ + project date format
      // so counselor sees the same wall-clock they see on the card above.
      const fromLabel = p.from?.date
        ? `${formatDate(p.from.date)}${p.from?.startTime ? `, ${fmt12(p.from.startTime)}` : ''}`
        : '—';
      const toLabel = p.to?.date
        ? `${formatDate(p.to.date)}${p.to?.startTime ? `, ${fmt12(p.to.startTime)}` : ''}`
        : '—';
      text = (
        <span className="text-slate-500">
          Rescheduled from {fromLabel} → <b>{toLabel}</b>
          {bookingTz ? <span className="text-slate-400"> ({bookingTz})</span> : null}
        </span>
      );
      break;
    }
    case 'meeting_booked_from_public':
      text = <span className="text-slate-500">Booked via public calendar link</span>;
      break;
    case 'meeting_booked_from_lead':
      text = <span className="text-slate-500">Booked from lead detail</span>;
      break;
    default:
      text = <span className="text-slate-400">{activity.actionType}</span>;
  }
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        {text}
        <p className="text-[10px] text-slate-400 mt-0.5">
          {activity.actor?.name ? `${activity.actor.name} · ` : ''}
          {formatDateTime(activity.createdAt)}
        </p>
      </div>
    </div>
  );
}
